import * as api from "./api";
import * as drwApi from "./drwApi";
import { datadogRum } from "@datadog/browser-rum";

/**
 * Maps feature flag string values (method names) to actual API functions.
 * Feature flags return the method name to call, directing traffic to either the
 * old (api.js) or new DRW (drwApi.ts) endpoint.
 *
 * Usage:
 *   const methodName = featureFlags["enableRescueTrackerStatusMethod"]; // e.g. "getVerifiedRescueTrackerStatusDRW" or "getVerifiedRescueTrackerStatus"
 *   const result = await apiMethodMap[methodName](params);
 */
export const apiMethodMap: Record<string, (...args: any[]) => any> = {
  // Rescue Tracker Status — args: (partnerCode, usageId, callId). The legacy key keeps its
  // LaunchDarkly variation name but resolves to the verified (callId-checked) endpoint.
  getVerifiedRescueTrackerStatus: api.getVerifiedRescueTrackerStatus,
  getVerifiedRescueTrackerStatusDRW: drwApi.getVerifiedRescueTrackerStatusDRW,

  // Partner Details
  getPartnerDetailsApi: api.getPartnerDetailsApi,
  getPartnerDetailsApiDRW: drwApi.getPartnerDetailsApiDRW,
};

/**
 * Determines whether the resolved method targets the DRW endpoint.
 */
function isDrwMethod(methodName: string): boolean {
  return methodName.toLowerCase().endsWith("drw");
}

/**
 * Resolves and invokes the correct API method based on a feature flag value.
 * Logs Datadog actions on successful resolution and errors on failures.
 * @param methodName - The feature flag value (function name string)
 * @param args - Arguments to pass to the resolved function
 * @throws Error if the method name is not found in the map
 */
export function resolveApiMethod<T = any>(methodName: string, ...args: any[]): T {
  const method = apiMethodMap[methodName];
  if (!method) {
    const error = new Error(`[apiMethodResolver] Unknown API method: "${methodName}"`);
    datadogRum.addError(error, {
      methodName,
      availableMethods: Object.keys(apiMethodMap),
    });
    throw error;
  }

  const endpoint = isDrwMethod(methodName) ? "DRW" : "legacy";

  datadogRum.addAction("[apiMethodResolver] API method resolved", {
    methodName,
    endpoint,
  });

  const result = method(...args);

  // If the method returns a promise, attach Datadog tracking to it
  if (result && typeof result.then === "function") {
    return result.then(
      (response: any) => {
        datadogRum.addAction("[apiMethodResolver] API call succeeded", {
          methodName,
          endpoint,
        });
        return response;
      },
      (err: any) => {
        datadogRum.addError(
          new Error(`[apiMethodResolver] API call failed: ${methodName}`),
          {
            methodName,
            endpoint,
            status: err?.response?.status,
            message: err?.message,
          }
        );
        throw err;
      }
    ) as T;
  }

  return result;
}
